import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Platform } from 'ionic-angular';

import { OnPremiseProvider } from './on-premise';
import { WheatleyProvider } from './wheatley';
import { ModalProvider } from './popup/modal';
import { TranslateService } from '@ngx-translate/core';

/**
 * This provider acts as a wrapper
 * around the BrainBuilder API.
 */
@Injectable()
export class API {
    constructor(
        public http: HttpClient,
        public platform: Platform,
        private modalProvider: ModalProvider,
        public onPremiseProvider: OnPremiseProvider,
        public wheatleyProvider: WheatleyProvider,
        private translate: TranslateService
    ) {
        if (this.getEnvironment() == 'onpremiseelectron') {
            this.baseURL = this.onPremiseProvider.getBrainBankHostAddress();
        } else if (this.getEnvironment() == 'development') {
            this.baseURL = 'http://localhost:9022';
            this.dev = true;
        } else if (this.getEnvironment() == 'staging') {
            this.baseURL = 'https://brainbank-staging.neurala.com';
        } else if (this.getEnvironment() == 'releasecandidate') {
            this.baseURL = 'https://brainbank-rc.neurala.com';
        } else if (this.getEnvironment() == 'production') {
            this.baseURL = 'https://brainbank.neurala.com';
        }
    }

    private baseURL: string;
    private dev = false;
    private userToken: string;
    private wheatleyToken: string;
    private loggedIn = false;
    private environment: string | null = null;

    public getEnvironment(): string {
        if (this.environment == null) {
            if (this.onPremiseProvider.isOnPremiseElectronApp()) {
                this.environment = 'onpremiseelectron';
            } else if (window.location.hostname == 'localhost') {
                this.environment = 'development';
            } else if (window.location.hostname.includes('brainbuilder-staging.neurala.com')) {
                this.environment = 'staging';
            } else if (
                window.location.hostname.includes('brainbuilder-app-staging.s3-website.us-east-2.amazonaws.com')
            ) {
                this.environment = 'staging';
            } else if (window.location.hostname.includes('brainbuilder-rc.neurala.com')) {
                this.environment = 'releasecandidate';
            } else {
                this.environment = 'production';
            }
        }

        return this.environment;
    }

    public isOnline(): boolean {
        return navigator.onLine;
    }

    public getParameterByName(name: string): string {
        const url = window.location.href;
        name = name.replace(/[\[\]]/g, '\\$&');
        const regex = new RegExp(`[?&]${name}(=([^&#]*)|&|#|$)`);
        const results = regex.exec(url);

        if (!results) {
            return '';
        }

        if (!results[2]) {
            return '';
        }

        return decodeURIComponent(results[2].replace(/\+/g, ' '));
    }

    public getUserToken(): string {
        this.userToken = this.getParameterByName('token');

        return this.userToken;
    }

    public endpoint(): string {
        return this.baseURL;
    }

    public isLoggedIn(): boolean {
        return this.loggedIn;
    }

    public async getWheatleyToken(): Promise<string> {
        if (!this.wheatleyToken) {
            this.wheatleyToken = await this.wheatleyProvider.getToken();
        }

        return this.wheatleyToken;
    }

    /**
     * Creates the necessary headers for all requests
     * Other libraries like Resumable have no clue what HttpHeaders
     * are, so we just use an object to define the headers
     */
    public async createHeaders(): Promise<any> {
        const wheatleyToken = await this.getWheatleyToken();

        // create a header with token when avaiable
        return wheatleyToken ? { Authorization: `WheatleyToken ${wheatleyToken}` } : {};
    }

    private handleError(err: HttpErrorResponse): void {
        // If BrainBank fails, send to error page
        if (this.getEnvironment() == 'onpremiseelectron') {
            if (err.status == 0) {
                setTimeout(() => {
                    /**
                     * See BB-5414
                     * If we get a status of 0 it means either BrainBank is unresponsive
                     * or a request is taking too long and BrainBuilder hangs up the connection.
                     * Run a healthcheck so we can see if BrainBank is actually unresponsive or
                     * if it is just a single bad response.  Then the onpremiseProvider can manage
                     * the details of what to do.
                     */
                    this.onPremiseProvider.healthcheck();
                }, 0);
            }
        }

        throw err;
    }

    /**
     * Creates a GET request on the Neurala API
     * @param endpoint: string - API Endpoint that request should be pointed at
     */
    public async get(endpoint: string): Promise<any> {
        try {
            return await new Promise(async (resolve, reject) => {
                this.http
                    .get<any>(`${this.baseURL}/${endpoint}`, {
                        headers: await this.createHeaders()
                    })
                    .subscribe(
                        data => (data.success ? resolve(data) : reject(data)),
                        err => reject(err)
                    );
            });
        } catch (err) {
            this.handleError(err);
        }
    }

    /**
     * Creates a POST request on the Neurala API
     * @param endpoint: string - API Endpoint that request should be pointed at
     * @param payload: Object - Any data to be supplied in the POST request
     */
    public async post(endpoint: string, payload: any = {}): Promise<any> {
        try {
            return await new Promise(async (resolve, reject) => {
                this.http
                    .post<any>(`${this.baseURL}/${endpoint}`, payload, {
                        headers: await this.createHeaders()
                    })
                    .subscribe(
                        data => (data.success ? resolve(data) : reject(data)),
                        err => reject(err)
                    );
            });
        } catch (err) {
            this.handleError(err);
        }
    }

    /**
     * Creates a PATCH request on the Neurala API
     * @param endpoint: string - API Endpoint that request should be pointed at
     * @param payload: Object - Any data to be supplied in the PATCH request
     */
    public async patch(endpoint: string, payload: any = {}): Promise<any> {
        try {
            return await new Promise(async (resolve, reject) => {
                this.http
                    .patch<any>(`${this.baseURL}/${endpoint}`, payload, {
                        headers: await this.createHeaders()
                    })
                    .subscribe(
                        data => (data.success ? resolve(data) : reject(data)),
                        err => reject(err)
                    );
            });
        } catch (err) {
            this.handleError(err);
        }
    }

    /**
     * Creates a DELETE request on the Neurala API
     * @param endpoint: string - API Endpoint that request should be pointed at
     */
    public async delete(endpoint: string): Promise<any> {
        try {
            return await new Promise(async (resolve, reject) => {
                this.http
                    .delete<any>(`${this.baseURL}/${endpoint}`, {
                        headers: await this.createHeaders()
                    })
                    .subscribe(
                        data => (data.success ? resolve(data) : reject(data)),
                        err => reject(err)
                    );
            });
        } catch (err) {
            this.handleError(err);
        }
    }

    /**
     * Creates a PUT request on the Neurala API
     * @param endpoint: string - API Endpoint that request should be pointed at
     * @param payload: Object - Any data to be supplied in the PUT request
     */
    public async put(endpoint: string, payload: any = {}): Promise<any> {
        try {
            return await new Promise(async (resolve, reject) => {
                this.http
                    .put<any>(`${this.baseURL}/${endpoint}`, payload, {
                        headers: await this.createHeaders()
                    })
                    .subscribe(
                        data => (data.success ? resolve(data) : reject(data)),
                        err => reject(err)
                    );
            });
        } catch (err) {
            this.handleError(err);
        }
    }

    /**
     * Handles all error reporting to the user
     * @param err: Error object to be logged to the console in dev
     * @param title: Alert modal title
     * @param message: Alert modal message
     */
    private errorHandler(err: any, title: string, message: string): void {
        if (this.dev) {
            console.log(err);
        }

        this.modalProvider.presentError(title, message, undefined, this.translate.instant('Okay'));

        throw err;
    }

    /**
     * Removes apikey of the user from angular session and cookie
     */
    public async resetInfo(): Promise<void> {
        await this.wheatleyProvider.deleteToken();
        this.wheatleyToken = '';
    }

    /**
     * Logs a user into the Wheatley Service
     * @param email - Email address for account
     * @param password - Password for account
     *
     * @return any - Response to login request
     */
    public async login(email: string, password: string): Promise<any> {
        try {
            const login = await this.post('auth', {
                email,
                password
            });

            // Set Api token for other requests
            await this.setWheatleyToken(login.message);

            return login;
        } catch (e) {
            if (e.status == 403) {
                this.errorHandler(
                    e,
                    this.translate.instant('Access Denied'),
                    this.translate.instant(
                        'Sorry, but it looks like your account has been deactivated. Please contact your administrator.'
                    )
                );
            } else if (e.status == 429) {
                throw e;
            } else if (e.status < 500 && e.status >= 400) {
                this.errorHandler(
                    e,
                    this.translate.instant('Invalid Login'),
                    this.translate.instant('Your email or password is incorrect.')
                );
            }
            throw e;
        }
    }

    public async setWheatleyToken(newToken: string): Promise<void> {
        this.wheatleyToken = newToken;
        this.wheatleyProvider.setToken(newToken);
        this.loggedIn = true;
    }

    /**
     * Attempt to create a Brain Builder account
     */
    public async register(userObject: any): Promise<any> {
        try {
            userObject.token = this.userToken;
            return await this.post('users', userObject);
        } catch (err) {
            this.errorHandler(err, 'Error', err.error.message);
            return false;
        }
    }

    /**
     * Attempt to create a onpremise Brain Builder account
     */
    public async registerOnpremise(userObject: any): Promise<any> {
        try {
            userObject.token = this.userToken;
            return await this.post('onpremise/users', userObject);
        } catch (err) {
            throw err.error;
        }
    }

    /**
     * join organization and migrate data
     */
    public async joinOrganization(userObject: any): Promise<any> {
        try {
            userObject.token = this.userToken;
            return await this.post('organization/join', userObject);
        } catch (err) {
            this.errorHandler(err, 'Error', err.error.message);
            return false;
        }
    }

    public async validateUserToken(token: string): Promise<any> {
        try {
            return await this.post('token/validate', { token });
        } catch (err) {
            throw err;
        }
    }

    /**
     * Logs a user into Brain Bank application using authorization token
     *
     * @return NeuralaReponse - Response to validate token request
     */
    public async validateToken(): Promise<any> {
        try {
            const token = await this.getWheatleyToken();

            if (!token) {
                throw new Error('no-token');
            }

            const result = await this.post('auth/validate');
            this.loggedIn = true;

            return result;
        } catch (e) {
            throw e;
        }
    }
}



// WEBPACK FOOTER //
// ./src/providers/api.ts